/**
 * PartnerListingWizard
 *
 * Unified comprehensive listing form for all 4 asset types
 * (property / car / yacht / interior_design).
 *
 * - Schema-driven: fetches the template from /api/partner-listings/template/{type}
 * - Currency LOCKED to the partner's country (server-enforced) — never user-editable
 * - Live completion progress per section + overall %
 * - Multi-step section navigation
 * - Required-field highlighting + publish gate
 * - Media upload via existing /api/partner/upload-media
 *
 * Props:
 *   assetType: 'property' | 'car' | 'yacht' | 'interior_design'
 *   listingId: optional existing listing id
 *   onSaved: optional callback after save
 */
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle2, AlertCircle, Loader2, Save, Send, X,
  ChevronRight, Sparkles, Lock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { StyledSelect } from '@/components/ui/styled-select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PartnerListingWizard = ({ assetType = 'property', listingId: initialId, onSaved }) => {
  const [template, setTemplate] = useState(null);
  const [listing, setListing] = useState(null);
  const [listingId, setListingId] = useState(initialId || null);
  const [data, setData] = useState({});
  const [activeSection, setActiveSection] = useState(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [report, setReport] = useState(null);

  // Load template + existing listing if any
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const tplRes = await axios.get(`${API}/partner-listings/template/${assetType}`);
        if (cancelled) return;
        setTemplate(tplRes.data);
        setActiveSection(tplRes.data.section_order.find((s) =>
          tplRes.data.fields.some((f) => f.section === s)
        ));
        if (listingId) {
          const lRes = await axios.get(`${API}/partner-listings/${assetType}/${listingId}`);
          if (!cancelled) {
            setListing(lRes.data);
            setData(lRes.data);
          }
        }
      } catch (e) {
        toast.error('Failed to load form');
      }
    })();
    return () => { cancelled = true; };
  }, [assetType, listingId]);

  // Sections to render (only those that have fields)
  const sections = useMemo(() => {
    if (!template) return [];
    return template.section_order
      .map((s) => ({
        key: s,
        label: template.section_labels[s] || s,
        fields: template.fields.filter((f) => f.section === s),
      }))
      .filter((s) => s.fields.length > 0);
  }, [template]);

  const update = (field, value) => setData((d) => ({ ...d, [field]: value }));

  // Section completion (client-side preview)
  const sectionStats = useMemo(() => {
    const out = {};
    sections.forEach((s) => {
      const required = s.fields.filter((f) => f.required);
      const filled = required.filter((f) => isFilled(data[f.id], f.type));
      out[s.key] = {
        required_total: required.length,
        required_filled: filled.length,
        complete: required.length === filled.length,
        total: s.fields.length,
        filled: s.fields.filter((f) => isFilled(data[f.id], f.type)).length,
      };
    });
    return out;
  }, [sections, data]);

  const overallPct = useMemo(() => {
    if (!sections.length) return 0;
    const totalReq = sections.reduce((a, s) => a + sectionStats[s.key].required_total, 0);
    const filled = sections.reduce((a, s) => a + sectionStats[s.key].required_filled, 0);
    return totalReq === 0 ? 100 : Math.round((filled / totalReq) * 100);
  }, [sections, sectionStats]);

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      if (listingId) {
        const { data: res } = await axios.put(`${API}/partner-listings/${assetType}/${listingId}`, data);
        setListing(res.listing);
        toast.success('Draft saved');
      } else {
        const { data: res } = await axios.post(`${API}/partner-listings/${assetType}`, data);
        setListingId(res.listing.id);
        setListing(res.listing);
        toast.success('Draft created');
      }
      // Re-validate from server
      const vid = listingId || (listing?.id);
      if (vid) {
        const { data: v } = await axios.post(`${API}/partner-listings/${assetType}/${vid}/validate`);
        setReport(v);
      }
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!listingId) {
      toast.error('Save the draft first');
      return;
    }
    setPublishing(true);
    try {
      await axios.post(`${API}/partner-listings/${assetType}/${listingId}/publish`);
      toast.success('Listing published');
      onSaved && onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === 'object' && detail?.completion_pct != null) {
        toast.error(`Listing is ${detail.completion_pct}% complete — finish the highlighted fields first`);
      } else {
        toast.error(detail || 'Publish failed');
      }
    } finally {
      setPublishing(false);
    }
  };

  if (!template) {
    return (
      <div className="glass-card p-8 flex items-center gap-3 text-text-secondary">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading form…
      </div>
    );
  }

  const meta = template.country;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6" data-testid="partner-listing-wizard">
      {/* Sidebar — sections progress */}
      <aside className="glass-card p-5 self-start lg:sticky lg:top-24">
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-text-secondary mb-1">
            {template.asset_label}
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gold/10 ring-1 ring-gold/30 text-gold text-[10px] uppercase tracking-[0.15em] font-semibold">
              <Lock className="w-3 h-3" /> {meta.label}
            </span>
            <span className="text-text-secondary text-[11px]">· {meta.currency}</span>
          </div>
        </div>
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] uppercase tracking-[0.18em] text-text-secondary">Completion</span>
            <span className="text-sm font-semibold text-gold" data-testid="overall-completion-pct">{overallPct}%</span>
          </div>
          <div className="h-2 rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-gold/50 to-gold transition-all"
              style={{ width: `${overallPct}%` }}
            />
          </div>
        </div>
        <nav className="space-y-1">
          {sections.map((s) => {
            const st = sectionStats[s.key];
            const active = s.key === activeSection;
            return (
              <button
                key={s.key}
                onClick={() => setActiveSection(s.key)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between gap-2 transition-colors ${
                  active ? 'bg-white/10 text-white' : 'hover:bg-white/5 text-text-secondary'
                }`}
                data-testid={`section-nav-${s.key}`}
              >
                <span className="truncate">{s.label}</span>
                {st.complete ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                ) : st.required_total > 0 ? (
                  <span className="text-[10px] text-text-secondary">
                    {st.required_filled}/{st.required_total}
                  </span>
                ) : (
                  <span className="text-[10px] text-text-secondary">{st.filled}/{st.total}</span>
                )}
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Form section */}
      <div className="glass-card p-6 space-y-5">
        {sections
          .filter((s) => s.key === activeSection)
          .map((s) => (
            <motion.div
              key={s.key}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-5"
            >
              <header className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="font-playfair text-2xl font-semibold">{s.label}</h3>
                  <p className="text-text-secondary text-sm mt-0.5">
                    {sectionStats[s.key].required_filled}/{sectionStats[s.key].required_total} required ·{' '}
                    {sectionStats[s.key].filled}/{sectionStats[s.key].total} total
                  </p>
                </div>
                {sectionStats[s.key].complete && sectionStats[s.key].required_total > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 ring-1 ring-emerald-500/30 text-emerald-300 text-[10px] uppercase tracking-[0.15em] font-semibold">
                    <CheckCircle2 className="w-3 h-3" /> Section complete
                  </span>
                )}
              </header>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {s.fields.map((field) => (
                  <FieldRenderer
                    key={field.id}
                    field={field}
                    value={data[field.id]}
                    onChange={(v) => update(field.id, v)}
                    currency={meta.currency}
                  />
                ))}
              </div>
            </motion.div>
          ))}

        {/* Bottom actions */}
        <div className="border-t border-white/5 pt-5 flex flex-wrap gap-3 items-center justify-between">
          <div className="text-xs text-text-secondary flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-gold" />
            Currency, country & region are locked to your partner profile and cannot be changed.
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleSaveDraft}
              disabled={saving}
              variant="outline"
              className="border-white/10 hover:bg-white/5"
              data-testid="save-draft-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Draft
            </Button>
            <Button
              onClick={handlePublish}
              disabled={publishing || overallPct < 100}
              className="bg-gold hover:bg-gold/90 text-black disabled:opacity-40"
              data-testid="publish-listing-btn"
            >
              {publishing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              {overallPct < 100 ? `Publish (${overallPct}%)` : 'Publish'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Field renderer ──────────────────────────────────────────────────────────

const isFilled = (value, type) => {
  if (value == null) return false;
  if (type === 'multiselect' || type === 'media') return Array.isArray(value) && value.length > 0;
  if (type === 'boolean') return value !== undefined && value !== null;
  if (type === 'number' || type === 'currency') {
    const n = Number(value);
    return !Number.isNaN(n) && value !== '';
  }
  return String(value).trim().length > 0;
};

const FieldRenderer = ({ field, value, onChange, currency }) => {
  const wrapper = field.type === 'textarea' || field.type === 'media' || field.type === 'multiselect'
    ? 'md:col-span-2' : '';
  return (
    <div className={`${wrapper}`} data-testid={`field-${field.id}`}>
      <Label className="text-xs uppercase tracking-[0.16em] mb-1.5 flex items-center gap-1.5">
        {field.label}
        {field.required && <span className="text-gold">*</span>}
        {field.help && <span className="text-text-secondary text-[10px] normal-case tracking-normal italic">· {field.help}</span>}
      </Label>
      {renderInput(field, value, onChange, currency)}
    </div>
  );
};

const renderInput = (field, value, onChange, currency) => {
  const base = "bg-white/5 border-white/10";
  switch (field.type) {
    case 'textarea':
      return (
        <Textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          className={`${base} min-h-[80px]`}
          maxLength={field.max}
        />
      );
    case 'select':
      return (
        <StyledSelect
          value={value || ''}
          onChange={(v) => onChange(v)}
          placeholder="Select…"
          options={field.options || []}
          data-testid={`select-${field.id}`}
        />
      );
    case 'multiselect':
      return (
        <div className="flex flex-wrap gap-2">
          {(field.options || []).map((opt) => {
            const selected = Array.isArray(value) && value.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => {
                  const arr = Array.isArray(value) ? [...value] : [];
                  if (selected) onChange(arr.filter((v) => v !== opt));
                  else onChange([...arr, opt]);
                }}
                className={`px-3 py-1.5 rounded-full text-xs ring-1 transition-colors ${
                  selected
                    ? 'bg-gold/15 text-gold ring-gold/40'
                    : 'bg-white/[0.03] text-white/70 ring-white/10 hover:ring-white/30'
                }`}
              >
                {opt}
              </button>
            );
          })}
        </div>
      );
    case 'boolean':
      return (
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
          />
          Yes
        </label>
      );
    case 'currency':
      return (
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-text-secondary pointer-events-none">{currency}</span>
          <Input
            type="number"
            inputMode="decimal"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            className={`${base} pl-14`}
            placeholder="0"
          />
        </div>
      );
    case 'number':
      return (
        <Input
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={base}
          placeholder={field.placeholder}
        />
      );
    case 'url':
    case 'email':
    case 'tel':
    case 'text':
      return (
        <Input
          type={field.type === 'text' ? 'text' : field.type}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className={base}
          placeholder={field.placeholder}
        />
      );
    case 'media':
      return (
        <MediaField field={field} value={value} onChange={onChange} />
      );
    default:
      return (
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className={base}
        />
      );
  }
};

// Drag-and-drop media field — uploads files to /api/partner-listings/upload-media
// and stores the returned URLs. Supports multi-file parallel uploads with
// per-file progress, drag-to-reorder, and URL paste as a fallback.
const MediaField = ({ field, value, onChange }) => {
  const arr = Array.isArray(value) ? value : [];
  const [draft, setDraft] = useState('');
  const [uploads, setUploads] = useState([]); // [{id, name, pct, status, error}]
  const [dragOver, setDragOver] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const max = field.media?.max || 30;
  const accept = field.media?.accept || 'image';
  const uploading = uploads.some((u) => u.status === 'uploading');

  // Translate schema accept → mime + form media_type
  const acceptAttr = (() => {
    if (accept.includes('pdf') && accept.includes('image')) return 'image/*,application/pdf';
    if (accept === 'pdf') return 'application/pdf';
    if (accept === 'video') return 'video/*';
    if (accept === 'image|pdf') return 'image/*,application/pdf';
    return 'image/*';
  })();

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    if (arr.length + files.length > max) {
      toast.error(`Maximum ${max} items per field`);
      return;
    }

    // Initialise progress rows
    const queue = files.map((file) => ({
      id: `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 8)}`,
      name: file.name,
      size: file.size,
      pct: 0,
      status: 'uploading',
      error: null,
      file,
    }));
    setUploads((u) => [...u, ...queue]);

    // Parallel upload with per-file progress
    const results = await Promise.all(
      queue.map(async (item) => {
        const file = item.file;
        let mediaType = 'image';
        if (file.type.startsWith('video/')) mediaType = 'video';
        else if (file.type === 'application/pdf') mediaType = 'pdf';

        const form = new FormData();
        form.append('file', file);
        form.append('media_type', mediaType);
        try {
          const { data } = await axios.post(
            `${API}/partner-listings/upload-media`,
            form,
            {
              headers: { 'Content-Type': 'multipart/form-data' },
              onUploadProgress: (e) => {
                const pct = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
                setUploads((all) => all.map((x) => (x.id === item.id ? { ...x, pct } : x)));
              },
            },
          );
          setUploads((all) => all.map((x) => (x.id === item.id ? { ...x, status: 'done', pct: 100 } : x)));
          return data.url;
        } catch (e) {
          const msg = e?.response?.data?.detail || 'upload failed';
          setUploads((all) => all.map((x) => (x.id === item.id ? { ...x, status: 'error', error: msg } : x)));
          toast.error(`${file.name}: ${msg}`);
          return null;
        }
      }),
    );

    const ok = results.filter(Boolean);
    if (ok.length > 0) {
      onChange([...arr, ...ok]);
      toast.success(`Uploaded ${ok.length} file${ok.length > 1 ? 's' : ''}`);
    }
    // Auto-clear successful rows after a short delay; keep errors visible
    setTimeout(() => {
      setUploads((all) => all.filter((x) => x.status === 'uploading' || x.status === 'error'));
    }, 1500);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(Array.from(e.dataTransfer.files));
  };

  // Reorder previews via drag-and-drop (image drag, not file drag)
  const reorder = (from, to) => {
    if (from === to || from == null || to == null) return;
    const next = [...arr];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    onChange(next);
  };

  const apiOrigin = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const isImage = (url) => /\.(jpe?g|png|webp|gif|heic|heif)(\?|$)/i.test(url || '');
  const fullUrl = (url) => (url?.startsWith('http') ? url : `${apiOrigin}${url}`);

  return (
    <div className="space-y-3">
      {/* Drop zone */}
      <label
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`block cursor-pointer border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
          dragOver
            ? 'border-gold bg-gold/5'
            : 'border-white/15 hover:border-white/30 bg-white/[0.02]'
        } ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
        data-testid={`media-dropzone-${field.id}`}
      >
        <input
          type="file"
          multiple
          accept={acceptAttr}
          className="hidden"
          onChange={(e) => uploadFiles(Array.from(e.target.files))}
          disabled={uploading}
        />
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-gold text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Uploading…
          </div>
        ) : (
          <div className="text-sm text-text-secondary">
            <span className="text-gold font-medium">Click to upload</span> or drag and drop multiple files
            <p className="text-[10px] mt-1">{accept} · up to {max} items · {arr.length}/{max} used{arr.length > 1 ? ' · drag thumbnails to reorder, first is cover' : ''}</p>
          </div>
        )}
      </label>

      {/* URL paste fallback */}
      <details className="text-xs text-text-secondary">
        <summary className="cursor-pointer hover:text-white/80">Or paste a URL</summary>
        <div className="flex gap-2 mt-2">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="https://…"
            className="bg-white/5 border-white/10 text-xs"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              if (!draft.trim()) return;
              if (arr.length >= max) { toast.error(`Maximum ${max} items`); return; }
              onChange([...arr, draft.trim()]);
              setDraft('');
            }}
            className="border-white/10"
          >Add</Button>
        </div>
      </details>

      {/* Per-file upload progress */}
      {uploads.length > 0 && (
        <div className="space-y-1.5" data-testid={`media-progress-${field.id}`}>
          {uploads.map((u) => (
            <div key={u.id} className="text-[11px] bg-white/[0.03] ring-1 ring-white/10 rounded-md px-2.5 py-1.5">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="truncate text-text-secondary">{u.name}</span>
                <span className={`font-mono ${u.status === 'error' ? 'text-red-400' : u.status === 'done' ? 'text-emerald-400' : 'text-gold'}`}>
                  {u.status === 'error' ? 'failed' : u.status === 'done' ? 'done' : `${u.pct}%`}
                </span>
              </div>
              <div className="h-1 rounded-full bg-white/5 overflow-hidden">
                <div
                  className={`h-full transition-all ${u.status === 'error' ? 'bg-red-500/60' : 'bg-gold'}`}
                  style={{ width: `${u.status === 'error' ? 100 : u.pct}%` }}
                />
              </div>
              {u.error && <div className="text-red-400 text-[10px] mt-1">{u.error}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Previews */}
      {arr.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {arr.map((url, i) => (
            <div
              key={`${url}-${i}`}
              draggable
              onDragStart={() => setDragIndex(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); reorder(dragIndex, i); setDragIndex(null); }}
              className={`relative group rounded-lg overflow-hidden ring-1 ring-white/10 bg-white/5 aspect-square cursor-move ${
                dragIndex === i ? 'opacity-50' : ''
              }`}
              data-testid={`media-preview-${field.id}-${i}`}
            >
              {isImage(url) ? (
                <img src={fullUrl(url)} alt="" className="w-full h-full object-cover" loading="lazy" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center p-2 text-[10px] text-text-secondary">
                  <span className="font-mono break-all line-clamp-3 text-center">{url.split('/').pop() || url}</span>
                </div>
              )}
              {i === 0 && (
                <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded-full bg-gold text-black text-[9px] font-semibold uppercase tracking-wider">
                  Cover
                </span>
              )}
              <button
                onClick={() => onChange(arr.filter((_, idx) => idx !== i))}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Remove"
                data-testid={`media-remove-${field.id}-${i}`}
              >
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PartnerListingWizard;
